import EventEmitter from 'events';

import { BASE_LAMBDA_ENDPOINT } from '../envs';

export interface AppSettings {
  microphoneId: string;
  speakerId: string;
  baseLambdaEndpoint: string;
}

export interface MediaDevice {
  deviceId: string;
  label: string;
  kind: MediaDeviceKind;
}

const STORAGE_KEY = 'phone-settings';

export default class SettingsService extends EventEmitter {
  private settings: AppSettings | null = null;

  constructor() {
    super();
    this.loadSettings();
  }

  private loadSettings(): void {
    try {
      const storedSettings = localStorage.getItem(STORAGE_KEY);
      if (storedSettings) {
        this.settings = JSON.parse(storedSettings) as AppSettings;
      } else {
        this.settings = {
          microphoneId: 'default',
          speakerId: 'default',
          baseLambdaEndpoint: BASE_LAMBDA_ENDPOINT || '',
        };
      }
    } catch (error) {
      console.error('Failed to load settings from localStorage:', error);
      this.settings = {
        microphoneId: 'default',
        speakerId: 'default',
        baseLambdaEndpoint: BASE_LAMBDA_ENDPOINT || '',
      };
    }
  }

  private saveSettings(): void {
    if (this.settings) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    }
  }

  getSettings(): AppSettings {
    if (!this.settings) {
      this.loadSettings();
    }
    return this.settings || { microphoneId: '', speakerId: '', baseLambdaEndpoint: BASE_LAMBDA_ENDPOINT || '' };
  }

  updateSettings(settings: Partial<AppSettings>): AppSettings {
    this.settings = {
      ...this.getSettings(),
      ...settings,
    };
    this.saveSettings();
    this.emit('change', settings);

    return this.settings;
  }

  getBaseLambdaEndpoint(): string {
    const settings = this.getSettings();
    const endpoint = settings.baseLambdaEndpoint || BASE_LAMBDA_ENDPOINT || '';

    // Normalize URL by removing trailing slash
    return endpoint.replace(/\/$/, '');
  }

  restoreDefaultBaseLambdaEndpoint(): AppSettings {
    return this.updateSettings({
      baseLambdaEndpoint: BASE_LAMBDA_ENDPOINT || '',
    });
  }
}
